import { useState, useEffect, useCallback } from "react";
import { ref, onValue, set, remove, update, get } from "firebase/database";
import { database } from "../utils/firebase";
import { CategoryType, convertCategoryLanguage } from "../utils/categories";
import { useLanguage } from "../utils/useLanguage";

export interface ShoppingItem {
  id: string;
  name: string;
  category: CategoryType;
  sortOrder?: number;
}

export const sortShoppingItems = <T extends ShoppingItem>(
  itemsToSort: T[],
): T[] =>
  [...itemsToSort].sort((a, b) =>
    a.name.trim().localeCompare(b.name.trim(), undefined, {
      sensitivity: "base",
    }),
  );

const buildSortOrderUpdates = (itemsToSort: ShoppingItem[]) => {
  const updates: Record<string, number> = {};

  itemsToSort.forEach((item, index) => {
    updates[`${item.id}/sortOrder`] = index;
  });

  return updates;
};

/**
 * Normalize a category to Romanian (base language)
 * This ensures backward compatibility with items created in any language
 */
const normalizeCategoryToRo = (category: any): CategoryType => {
  if (!category) return "altele";
  const normalized = convertCategoryLanguage(category, "ro");
  return normalized || ("altele" as CategoryType);
};

/**
 * Custom hook to manage the state and Firebase interactions for a single shopping list.
 * It handles fetching data, adding, removing, checking, and sorting items.
 * @param listId The unique identifier for the shopping list.
 * @returns An object containing the list's state (items, name, etc.) and functions to manipulate it.
 */
export const useShoppingList = (
  listId: string | null,
  onListNotFound?: (listId: string) => void,
) => {
  // State for the items that are yet to be purchased
  const [items, setItems] = useState<ShoppingItem[]>([]);
  // State for the items that have been checked off the list
  const [checkedItems, setCheckedItems] = useState<ShoppingItem[]>([]);
  // State for the name of the shopping list
  const [listName, setListName] = useState<string>("Shopping List");
  // If error handling is planned for future use but not implemented yet:
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [error, setError] = useState<string>("");
  // State to indicate if the initial data is being loaded
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();

  useEffect(() => {
    // Reset state when listId changes
    setItems([]);
    setCheckedItems([]);
    setListName("Shopping List");
    setLoading(true);

    if (!listId) {
      setLoading(false);
      return;
    }

    const listRef = ref(database, `lists/${listId}`);

    // Perform an initial fetch to ensure all data is loaded before hiding the loader.
    get(listRef).then((snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.val();
        setListName(data.name || "Shopping List");

        const itemsArray = data.items
          ? sortShoppingItems(
              Object.entries(data.items).map(([id, item]: [string, any]) => ({
                id,
                ...item,
                category: normalizeCategoryToRo(item.category),
              })),
            )
          : [];
        setItems(itemsArray);

        const checkedArray = data.checkedItems
          ? sortShoppingItems(
              Object.entries(data.checkedItems).map(
                ([id, item]: [string, any]) => ({
                  id,
                  ...item,
                  category: normalizeCategoryToRo(item.category),
                }),
              ),
            )
          : [];
        setCheckedItems(checkedArray);
      } else {
        // If the list doesn't exist, call the handler.
        if (onListNotFound) {
          onListNotFound(listId);
        }
      }
      // All initial data is processed, set loading to false.
      setLoading(false);
    });

    // Set up real-time listeners for subsequent updates.

    const connectedRef = ref(database, ".info/connected");
    const unsubscribeConnected = onValue(connectedRef, (snapshot) => {
      if (snapshot.val() === false) {
        setError(
          language === "ro"
            ? "Ești offline. Te rugăm să te reconectezi inainte de a face alte modificări."
            : "You are offline. Please reconnect before making other updates.",
        );
      } else {
        // When connection is restored, briefly show loader to allow data to sync.
        setLoading(true);
        setError("");
        // Hide loader after a short delay to ensure UI has updated.
        setTimeout(() => setLoading(false), 1000);
      }
    });

    const unsubscribeListName = onValue(
      ref(database, `lists/${listId}/name`),
      (snapshot) => {
        setListName(snapshot.val() || "Shopping List"); // Don't set loading here
      },
    );

    const unsubscribeItems = onValue(
      ref(database, `lists/${listId}/items`),
      (snapshot) => {
        const data = snapshot.val() || {};
        const itemsArray = sortShoppingItems(
          Object.entries(data).map(([id, item]: [string, any]) => ({
            id,
            ...item,
            category: normalizeCategoryToRo(item.category),
          })),
        );
        setItems(itemsArray);
      },
    );

    const unsubscribeCheckedItems = onValue(
      ref(database, `lists/${listId}/checkedItems`),
      (snapshot) => {
        const data = snapshot.val() || {};
        const checkedArray = sortShoppingItems(
          Object.entries(data).map(([id, item]: [string, any]) => ({
            id,
            ...item,
            category: normalizeCategoryToRo(item.category),
          })),
        );
        setCheckedItems(checkedArray);
      },
    );

    // Cleanup function to unsubscribe from all Firebase listeners when the component unmounts or listId changes.
    return () => {
      unsubscribeConnected();
      unsubscribeListName();
      unsubscribeItems();
      unsubscribeCheckedItems();
    };
  }, [listId, onListNotFound, language]);

  /**
   * Adds a new item to the shopping list.
   * @param itemName The name of the item to add.
   */
  const addItem = useCallback(
    async (itemName: string, category: CategoryType) => {
      if (!listId) return;
      try {
        const newItem: ShoppingItem = {
          id: Date.now().toString(),
          name: itemName,
          category: category,
        };
        const nextItems = sortShoppingItems([...items, newItem]);
        const itemRef = ref(database, `lists/${listId}/items/${newItem.id}`);
        await set(itemRef, {
          ...newItem,
          sortOrder: nextItems.findIndex((item) => item.id === newItem.id),
        });
        await update(
          ref(database, `lists/${listId}/items`),
          buildSortOrderUpdates(nextItems),
        );
        setError(""); // Clear any previous errors
      } catch (err) {
        setError(
          `Failed to add item: ${
            err instanceof Error ? err.message : "Unknown error"
          }`,
        );
      }
    },
    [items, listId],
  );

  /**
   * Updates the name of the shopping list.
   * @param newName The new name for the list.
   */
  const updateListName = async (newName: string) => {
    if (!listId || newName.trim() === "") return;
    try {
      const listNameRef = ref(database, `lists/${listId}/name`);
      await set(listNameRef, newName.trim());
      setError(""); // Clear any previous errors
    } catch (err) {
      setError(
        `Failed to update list name: ${
          err instanceof Error ? err.message : "Unknown error"
        }`,
      );
    }
  };

  /**
   * Removes an item from the main shopping list.
   * @param itemId The ID of the item to remove.
   */
  const removeItem = async (itemId: string) => {
    if (!listId) return;
    try {
      await remove(ref(database, `lists/${listId}/items/${itemId}`));
      setError("");
    } catch (err) {
      setError(
        `Failed to remove item: ${
          err instanceof Error ? err.message : "Unknown error"
        }`,
      );
    }
  };

  /**
   * Removes an item from the checked items list.
   * @param itemId The ID of the item to remove.
   */
  const removeCheckedItem = async (itemId: string) => {
    if (!listId) return;
    await remove(ref(database, `lists/${listId}/checkedItems/${itemId}`));
  };

  /**
   * Moves an item from the main list to the checked list.
   * @param itemToCheck The shopping item to check off.
   */
  const checkItem = async (itemToCheck: ShoppingItem) => {
    if (!listId) return;

    // Optimistic update for a responsive UI
    const newItems = items.filter((item) => item.id !== itemToCheck.id);
    const newCheckedItems = [...checkedItems, itemToCheck];
    setItems(sortShoppingItems(newItems));
    setCheckedItems(sortShoppingItems(newCheckedItems));

    const updates: Record<string, any> = {};
    updates[`/lists/${listId}/items/${itemToCheck.id}`] = null; // Remove from items
    updates[`/lists/${listId}/checkedItems/${itemToCheck.id}`] = {
      // Add to checkedItems
      name: itemToCheck.name,
      category: itemToCheck.category,
    };

    try {
      await update(ref(database), updates);
    } catch (err) {
      setError(
        `Failed to check item: ${
          err instanceof Error ? err.message : "Unknown error"
        }`,
      );
      // Revert UI on error
      setItems(items);
      setCheckedItems(checkedItems);
    }
  };

  /**
   * Moves an item from the checked list back to the main list.
   * @param itemToUncheck The shopping item to uncheck.
   */
  const uncheckItem = async (itemToUncheck: ShoppingItem) => {
    if (!listId) return;

    // Optimistic update for a responsive UI
    const newCheckedItems = checkedItems.filter(
      (item) => item.id !== itemToUncheck.id,
    );
    const newItems = [...items, itemToUncheck];
    setCheckedItems(sortShoppingItems(newCheckedItems));
    setItems(sortShoppingItems(newItems));

    const updates: Record<string, any> = {};
    updates[`/lists/${listId}/checkedItems/${itemToUncheck.id}`] = null; // Remove from checked
    updates[`/lists/${listId}/items/${itemToUncheck.id}`] = {
      // Add to items
      name: itemToUncheck.name,
      category: itemToUncheck.category,
    };

    try {
      await update(ref(database), updates);
    } catch (err) {
      setError(
        `Failed to uncheck item: ${
          err instanceof Error ? err.message : "Unknown error"
        }`,
      );
      // Revert UI on error
      setCheckedItems(checkedItems);
      setItems(items);
    }
  };

  /**
   * Removes all items from the main shopping list.
   */
  const removeAllItems = async () => {
    if (!listId) return;
    await remove(ref(database, `lists/${listId}/items`));
  };

  /**
   * Removes all items from the checked items list.
   */
  const removeAllCheckedItems = async () => {
    if (!listId) return;
    await remove(ref(database, `lists/${listId}/checkedItems`));
  };

  // Expose the state and handler functions to the component.
  return {
    items,
    checkedItems,
    listName,
    error, // Keep error in the return object for components to display
    loading,
    addItem,
    updateListName,
    removeItem,
    removeCheckedItem,
    checkItem,
    uncheckItem,
    removeAllItems,
    removeAllCheckedItems,
  };
};
